import React, { useState, useEffect } from 'react';
import 'react-pivottable/pivottable.css';
import { Observer } from 'mobx-react';
import { css } from '@emotion/core';
import {
  Typography,
  Button,
  IconButton,
  ListItem,
  ListItemText,
  List,
  Divider,
} from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import DeleteIcon from '@material-ui/icons/Delete';
import CirclePlusIcon from '@material-ui/icons/AddCircleOutline';
import { dateSort } from '../../util/TableUtils';
import { useRootStore } from '../../../stores/BridgeProvider';
import { WideBox, FlexDiv } from '../../styles/styles';
import Spinner from '../../util/Spinner';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import RenderPropModal from '../../dealMapper/common/RenderPropModal';
import ConfirmDialog from '../../dealMapper/common/ConfirmDialog';

const NewReportOptions = css`
  display: flex;
  flex-direction: column;
  a {
    font-size: 16px;
    color: inherit;
    margin: 5px 0px;
  }
`;

const Reports = () => (
  <Observer
    render={() => {
      const history = useHistory();
      const { dataStore, authStore, orgStore } = useRootStore();
      const [isLoading, setLoading] = useState(true);
      const [selectedReport, setSelectedReport] = useState(null);
      const [reportTemplates, setReportTemplates] = useState([]);
      const transactionCategories = dataStore.validTransactionCategoryArray;

      const { dashboardConfig } = orgStore;

      // const updateDashboardPref = async ({ TemplateId }) => {
      //   const clonePref = [...dashboardConfig];
      //   const pIndex = dashboardConfig.findIndex(x => x.Name === 'Reports');
      //   const cloneVisRep = [...clonePref[pIndex].VisibleReports];
      //   const prefIndex = cloneVisRep.findIndex(
      //     item => item.TemplateId === TemplateId,
      //   );
      //   if (prefIndex > -1) {
      //     cloneVisRep.splice(prefIndex, 1);
      //     clonePref[pIndex].VisibleReports = cloneVisRep;
      //   }
      //   const payload = JSON.stringify(clonePref);
      //   try {
      //     await authStore.request.post(
      //       `${process.env.REACT_APP_API}/api/config`,
      //       {
      //         dashboardConfig: payload,
      //       },
      //     );
      //   } catch (e) {
      //     console.log(e);
      //   }
      // };

      const getReportTemplates = async () => {
        try {
          const { data } = await authStore.request.get(
            `${process.env.REACT_APP_API}/api/report-templates`,
          );
          setReportTemplates(data);
        } catch (e) {
          console.error(e);
        }
      };

      const deleteReportTemplate = async id => {
        try {
          await authStore.request.delete(
            `${process.env.REACT_APP_API}/api/report-template/`,
            {
              data: { ReportTemplateId: id },
            },
          );

          // updateDashboardPref({ TemplateId: id });

          getReportTemplates();
        } catch (e) {
          console.error(e);
        }
      };

      useEffect(() => {
        const initialize = async () => {
          const requests = [];
          if (transactionCategories.length === 0) {
            requests.push(dataStore.retrieveTransactionCategories());
          }
          requests.push(getReportTemplates());
          await Promise.all(requests);
          setLoading(false);
        };
        initialize();
      }, []);

      if (isLoading) return <Spinner />;
      return (
        <FlexDiv>
          <WideBox style={{ maxWidth: '1200px', width: '98%' }}>
            <ConfirmDialog
              title="Delete Report?"
              actions={({ handleClose }) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    flex: 1,
                  }}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      deleteReportTemplate(selectedReport);
                      handleClose();
                    }}
                  >
                    Yes
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={() => {
                      setSelectedReport(null);
                      handleClose();
                    }}
                  >
                    No
                  </Button>
                </div>
              )}
            >
              {({ handleOpen: handleOpenDelete }) => {
                const columns = [
                  {
                    Header: 'Report Name',
                    accessor: 'Name',
                    Filter: FilterPlaceholder,
                  },
                  {
                    Header: 'Type',
                    accessor: 'Type',
                  },
                  {
                    Header: 'Last Modified By',
                    accessor: 'ModifiedByName',
                  },
                  {
                    Header: 'Modified At',
                    accessor: d => format(d.ModifiedTimestamp, 'MM/DD/YYYY'),
                    id: 3,
                    sortType: dateSort,
                    width: 80,
                  },
                  {
                    Header: '',
                    Cell: ({ row: { original } }) => (
                      <IconButton
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedReport(original.TemplateId);
                          handleOpenDelete();
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ),
                    id: 4,
                    width: 30,
                  },
                ];

                return (
                  <CustomTable
                    data={reportTemplates}
                    columns={columns}
                    minRows={1}
                    trClassName="react-table-hover-row"
                    getTrProps={({ row }) => ({
                      onClick: () =>
                        history.push(`/report/${row.original.TemplateId}`),
                    })}
                    tableHeader={<Typography variant="h2">Reports</Typography>}
                    actionables={
                      <RenderPropModal
                        cardDisplay={({ handleClose }) => (
                          <div>
                            <Typography
                              variant="h4"
                              gutterBottom
                              style={{ textAlign: 'center' }}
                            >
                              Report Type
                            </Typography>
                            <Divider />
                            <div css={NewReportOptions}>
                              <ListItem
                                dense
                                button
                                component={Link}
                                to="/report/new/deal"
                              >
                                <ListItemText
                                  primary="Deal"
                                  primaryTypographyProps={{
                                    style: { fontSize: '16px' },
                                  }}
                                />
                              </ListItem>
                              <ListItem
                                button
                                dense
                                component={Link}
                                to="/report/new/dealTran"
                              >
                                <ListItemText
                                  primary="Deal Transaction"
                                  primaryTypographyProps={{
                                    style: { fontSize: '16px' },
                                  }}
                                />
                              </ListItem>
                              <ListItem
                                dense
                                button
                                component={Link}
                                to="/report/new/dealTranAcc"
                              >
                                <ListItemText
                                  primary="Deal Accounting Transaction"
                                  primaryTypographyProps={{
                                    style: { fontSize: '16px' },
                                  }}
                                />
                              </ListItem>
                            </div>
                          </div>
                        )}
                      >
                        {({ handleOpen }) => (
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleOpen}
                          >
                            <CirclePlusIcon
                              style={{ marginRight: '4px', marginLeft: '-3px' }}
                            />
                            New Report
                          </Button>
                        )}
                      </RenderPropModal>
                    }
                    containerStyle={{ padding: '0 2rem' }}
                  />
                );
              }}
            </ConfirmDialog>
          </WideBox>
        </FlexDiv>
      );
    }}
  />
);

export default Reports;

// startDate, endDate, transactionCategoriesID array

// report-template

// report-templates
